import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { CONFIG, authHeaders, authFetch } from '../utils/config';
import { QRCodeService } from '../utils/qrCodeService';
import { VCardService } from '../services/vcardService';
import { useTypist } from '../effects/useTypist';
import { useIsMobile } from '../hooks/useIsMobile';
import TileSelector from './TileSelector';
import { useContent } from '../hooks/useContent';

function VCardForm({ vcard, setVcard, content, isDark }) {
	return (
		<div className="space-y-3 mt-2">
			<div className="grid grid-cols-2 gap-3">
				{[['firstName', content.firstName ?? 'Prénom', content.firstNamePlaceholder ?? 'Jean'],
				  ['lastName',  content.lastName  ?? 'Nom',   content.lastNamePlaceholder  ?? 'Dupont']].map(([f, l, ph]) => (
					<div key={f} className={isDark ? 'text-white' : 'text-gray-900'}>
						<label className="form-label text-sm">{l}</label>
						<input className="form-input" type="text" placeholder={ph} value={vcard[f]}
							onChange={(e) => setVcard((p) => ({ ...p, [f]: e.target.value }))} />
					</div>
				))}
			</div>
			<div className="grid grid-cols-2 gap-3">
				{[['phone',     content.phone     ?? 'Téléphone',  content.phonePlaceholder     ?? '+33 6 00 00 00 00'],
				  ['phoneWork', content.phoneWork ?? 'Tél. travail', content.phoneWorkPlaceholder ?? '+33 1 00 00 00 00']].map(([f, l, ph]) => (
					<div key={f} className={isDark ? 'text-white' : 'text-gray-900'}>
						<label className="form-label text-sm">{l}</label>
						<input className="form-input" type="tel" placeholder={ph} value={vcard[f]}
							onChange={(e) => setVcard((p) => ({ ...p, [f]: e.target.value }))} />
					</div>
				))}
			</div>
			<div className={isDark ? 'text-white' : 'text-gray-900'}>
				<label className="form-label text-sm">{content.phoneMobile ?? 'Mobile'}</label>
				<input className="form-input" type="tel" placeholder={content.phoneMobilePlaceholder ?? '+33 7 00 00 00 00'} value={vcard.phoneMobile}
					onChange={(e) => setVcard((p) => ({ ...p, phoneMobile: e.target.value }))} />
			</div>
			<div className={isDark ? 'text-white' : 'text-gray-900'}>
				<label className="form-label text-sm">{content.mail ?? 'E-mail'}</label>
				<input className="form-input" type="email" placeholder={content.mailPlaceholder ?? 'jean@example.com'} value={vcard.mail}
					onChange={(e) => setVcard((p) => ({ ...p, mail: e.target.value }))} />
			</div>
			<div className={isDark ? 'text-white' : 'text-gray-900'}>
				<label className="form-label text-sm">{content.website ?? 'Site web'}</label>
				<input className="form-input" type="url" placeholder={content.websitePlaceholder ?? 'https://example.com'} value={vcard.website}
					onChange={(e) => setVcard((p) => ({ ...p, website: e.target.value }))} />
			</div>
			<div className="grid grid-cols-2 gap-3">
				{[['company',  content.company  ?? 'Entreprise', content.companyPlaceholder  ?? 'Ma Société'],
				  ['jobTitle', content.jobTitle ?? 'Poste',      content.jobTitlePlaceholder ?? 'Développeur']].map(([f, l, ph]) => (
					<div key={f} className={isDark ? 'text-white' : 'text-gray-900'}>
						<label className="form-label text-sm">{l}</label>
						<input className="form-input" type="text" placeholder={ph} value={vcard[f]}
							onChange={(e) => setVcard((p) => ({ ...p, [f]: e.target.value }))} />
					</div>
				))}
			</div>
			<div className={isDark ? 'text-white' : 'text-gray-900'}>
				<label className="form-label text-sm">{content.street ?? 'Rue'}</label>
				<input className="form-input" type="text" placeholder={content.streetPlaceholder ?? '12 rue de la Paix'} value={vcard.street}
					onChange={(e) => setVcard((p) => ({ ...p, street: e.target.value }))} />
			</div>
			<div className="grid grid-cols-2 gap-3">
				{[['city',  content.city  ?? 'Ville',  content.cityPlaceholder  ?? 'Paris'],
				  ['state', content.state ?? 'Région', content.statePlaceholder ?? 'Île-de-France']].map(([f, l, ph]) => (
					<div key={f} className={isDark ? 'text-white' : 'text-gray-900'}>
						<label className="form-label text-sm">{l}</label>
						<input className="form-input" type="text" placeholder={ph} value={vcard[f]}
							onChange={(e) => setVcard((p) => ({ ...p, [f]: e.target.value }))} />
					</div>
				))}
			</div>
			<div className="grid grid-cols-2 gap-3">
				{[['zipcode', content.zipcode ?? 'Code postal', content.zipcodePlaceholder ?? '75001'],
				  ['country', content.country ?? 'Pays',        content.countryPlaceholder  ?? 'France']].map(([f, l, ph]) => (
					<div key={f} className={isDark ? 'text-white' : 'text-gray-900'}>
						<label className="form-label text-sm">{l}</label>
						<input className="form-input" type="text" placeholder={ph} value={vcard[f]}
							onChange={(e) => setVcard((p) => ({ ...p, [f]: e.target.value }))} />
					</div>
				))}
			</div>
			<div className={isDark ? 'text-white' : 'text-gray-900'}>
				<label className="form-label text-sm">{content.birthday ?? 'Date de naissance'}</label>
				<input className="form-input" type="date" value={vcard.birthday}
					onChange={(e) => setVcard((p) => ({ ...p, birthday: e.target.value }))} />
			</div>
			<div className={isDark ? 'text-white' : 'text-gray-900'}>
				<label className="form-label text-sm">{content.personalNote ?? 'Note'}</label>
				<textarea className="form-input resize-none" rows={3} placeholder={content.personalNotePlaceholder ?? 'Infos supplémentaires…'} value={vcard.personalNote}
					onChange={(e) => setVcard((p) => ({ ...p, personalNote: e.target.value }))} />
			</div>
		</div>
	);
}

const buildFullName = (first = '', last = '') => {
	const f = first.trim(), l = last.trim();
	if (!f && !l) return '';
	return `${f} ${l}`.trim();
};

const StepCard = ({ children, isDark }) => (
	<div className={`rounded-xl border p-4 step-enter ${
		isDark ? 'bg-dark-750 border-dark-600' : 'bg-gray-50 border-gray-200'
	}`}>
		{children}
	</div>
);

const StepSummary = ({ n, title, summary, onEdit, isDark }) => (
	<div className="flex items-center gap-2 flex-wrap">
		<span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white text-xs font-bold shrink-0">✓</span>
		<span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</span>
		{summary && (
			<span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
				→ <span className="font-semibold text-[rgb(13,200,207)]">{summary}</span>
			</span>
		)}
		<button type="button" onClick={onEdit} className="ml-auto text-xs text-primary-400 hover:text-primary-300 underline shrink-0">
			Modifier
		</button>
	</div>
);

const StepTitle = ({ n, title, isDark }) => (
	<div className="flex items-center gap-2 mb-3">
		<span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[rgb(13,200,207)] text-white text-xs font-bold shrink-0">{n}</span>
		<span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</span>
	</div>
);

const ContinueBtn = ({ onClick, disabled }) => (
	<button type="button" onClick={onClick} disabled={disabled}
		className="mt-4 w-full py-2 rounded-lg font-medium text-sm bg-[rgb(13,200,207)] hover:bg-[rgb(10,170,176)] text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
		Continuer →
	</button>
);

const DecisionButtons = ({ onKeep, onDiscard, isDiscarding, isDark, keepQuestion }) => (
	<div className={`flex flex-col sm:flex-row items-center justify-center gap-3 p-4 rounded-xl border step-enter ${
		isDark ? 'bg-gradient-to-r from-dark-800 to-dark-700 border-dark-600' : 'bg-gradient-to-r from-gray-50 to-white border-gray-200 shadow-sm'
	}`}>
		<span className={`text-sm font-medium mb-2 sm:mb-0 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{keepQuestion}</span>
		<div className="flex gap-3">
			<button type="button" onClick={onKeep}
				className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium bg-gradient-to-r from-primary-500 to-primary-400 hover:from-primary-600 hover:to-primary-500 text-black shadow-md transition-all duration-200">
				<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
					<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
				</svg>
				Garder
			</button>
			<button type="button" onClick={onDiscard} disabled={isDiscarding}
				className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
					isDark ? 'bg-dark-600 hover:bg-dark-500 text-gray-300 hover:text-white border border-dark-500'
					: 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 shadow-sm'
				}`}>
				{isDiscarding ? (
					<svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
						<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
						<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
					</svg>
				) : (
					<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
						<path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
					</svg>
				)}
				{isDiscarding ? 'Suppression...' : 'Jeter'}
			</button>
		</div>
	</div>
);

const HD_PRESETS = {
	medium_300: { label: 'Medium 300 DPI', output_size: 1200, dpi: 300 },
	medium_600: { label: 'Medium 600 DPI', output_size: 2400, dpi: 300 },
	large_300:  { label: 'Large 300 DPI',  output_size: 2400, dpi: 600 },
};

const STRENGTH_BOUNDS = {
	// HD's highThreshold is an explicit value: past ~2.15 the QR pattern starts
	// visibly dominating the generated image.
	classique: { min: 0.85, max: 1.5, default: '1.3' },
	hd:        { min: 1.35, max: 2.5, default: '1.85', highThreshold: 2.16 },
};

// Outer 20% of the strength range is flagged as a caution zone, unless a bound
// defines an explicit lowThreshold/highThreshold override.
const STRENGTH_CAUTION_RATIO = 0.2;

const getStrengthThresholds = (bounds) => {
	const { min, max, lowThreshold, highThreshold } = bounds;
	const range = max - min;
	return {
		low: lowThreshold ?? min + range * STRENGTH_CAUTION_RATIO,
		high: highThreshold ?? max - range * STRENGTH_CAUTION_RATIO,
	};
};

const getStrengthZone = (value, bounds) => {
	const { low, high } = getStrengthThresholds(bounds);
	if (value <= low) return 'low';
	if (value >= high) return 'high';
	return 'ok';
};

const buildStrengthGradient = (bounds) => {
	const { min, max } = bounds;
	const { low, high } = getStrengthThresholds(bounds);
	const range = max - min;
	const lowPct = ((low - min) / range) * 100;
	const highPct = ((high - min) / range) * 100;
	const blend = 4;
	return `linear-gradient(to right, #f59e0b 0%, #f59e0b ${Math.max(lowPct - blend, 0)}%, #22c55e ${lowPct + blend}%, #22c55e ${Math.min(highPct - blend, 100)}%, #f59e0b ${Math.min(highPct + blend, 100)}%, #f59e0b 100%)`;
};

const SimplifiedAIQREditor = ({ onUserAcceptsQuikRCode }) => {
	const location = useLocation();
	const { isDark } = useTheme();
	const { content } = useContent('simplifiedAiQrEditor');
	const { content: qrEditorContent } = useContent('qrEditor');
	const { content: aiQrEditorContent } = useContent('aiQrEditor');
	const isMobile = useIsMobile();

	const ai = aiQrEditorContent || {
		fields: {
			promptAugLabel: 'Augmentation de prompt par IA',
			promptAugHint: 'Améliore automatiquement votre prompt pour de meilleurs résultats',
			positivePromptLabel: 'Ce que je veux',
			positivePromptPlaceholder: 'Ex : style futuriste, lignes néon, contraste élevé...',
			negativePromptLabel: 'Ce que je ne veux pas (optionnel)',
			negativePromptPlaceholder: 'Ex : flou, texte illisible, couleurs ternes...',
			maxUsageLabel: "Nombre maximal d'usage (optionnel)",
			validUntilLabel: 'Date de validité (optionnel)',
			validUntilHint: 'Le QuikR Code ne sera plus accessible après cette date',
			strengthLabel: "Visibilité du QR Code dans l'image (0.85 à 1.5, défaut 1.3)",
			strengthLabelHd: "Visibilité du QR Code dans l'image (1.35 à 2.50, défaut 1.85)",
			strengthLowWarning: "À ce niveau, le QuikR Code risque de manquer de lisibilité et d'être plus difficile à scanner.",
			strengthHighWarning: "À ce niveau, l'image générée risque d'être plus discrète, dominée par le motif du QR Code.",
			hdSlowHint: 'La génération HD est plus détaillée et peut prendre plusieurs minutes.',
			hdGeneratingHint: 'La génération HD peut prendre plusieurs minutes, merci de patienter.',
		},
	};

	const t = qrEditorContent || {
		tabs: { configuration: 'Configuration', colors: 'Couleurs', shapes: 'Formes & Styles' },
		fields: {
			urlHint: "L'URL doit commencer par https://",
			maxRedirectLabel: 'Nombre maximum de redirections (optionnel) :',
			maxRedirectPlaceholder: 'Illimité si vide',
			validUntilLabel: 'Date de validité (optionnel) :',
			validUntilHint: 'Le QR code ne sera plus accessible après cette date',
			boxSizeLabel: 'Taille des pixels :',
			borderSizeLabel: 'Taille de la bordure :',
			dotsColorLabel: 'Couleur des points :',
			bgColorLabel: "Couleur de l'arrière-plan :",
			dotsShapeLabel: 'Forme des points :',
			cornerRadiusLabel: 'Arrondi des coins :',
			spacingLabel: 'Espacement :',
		},
		shapes: { square: 'Carré', diamond: 'Losange', triangle: 'Triangle', pentagon: 'Pentagone', hexagon: 'Hexagone', octagon: 'Octogone', star: 'Étoile', heart: 'Cœur' },
	};

	const [completedUpTo, setCompletedUpTo] = useState(0);
	const stepStatus = (n) => n <= completedUpTo ? 'completed' : n === completedUpTo + 1 ? 'active' : 'hidden';
	const advanceTo  = (n) => setCompletedUpTo((p) => Math.max(p, n));
	const editStep   = (n) => {
		setCompletedUpTo(n - 1);
		setImageBase64(''); setShowDecisionButtons(false);
		setLivePreviewActive(false); setCurrentUuid(null);
		setError(''); setSuccess('');
	};

	const [usageData, setUsageData] = useState(null);
	const [usageLoading, setUsageLoading] = useState(true);

	const [codeType, setCodeType] = useState('quikr');
	const [quikrMode, setQuikrMode] = useState('classique'); // 'classique' | 'hd'
	const [hdPreset, setHdPreset] = useState('medium_300');
	// TODO: brancher canUseHD sur la subscription — le backend retourne 403 si is_default_formula && workflow != 'simple'
	const canUseHD = true;

	const [positivePrompt, setPositivePrompt] = useState('');
	const [negativePrompt, setNegativePrompt] = useState('');
	const [aiPromptAugmentation, setAiPromptAugmentation] = useState(true);
	const [netStrength, setNetStrength] = useState('1.3');
	const [maxRedirect, setMaxRedirect] = useState('');
	const [validUntil, setValidUntil] = useState('');

	const [qrStyle, setQrStyle] = useState({
		'dots-color': '#000000', 'background-color': '#ffffff',
		'dots-shape': 'square', 'dot-radius': 0, 'dot-spacing': 0,
		'border-size': 4, 'box-size': 10, 'max_redirect': '', 'valid_until': '',
	});
	const [activeQrTab, setActiveQrTab] = useState('config');

	const [dataContentType, setDataContentType] = useState('');

	const getInitialUrl = () => new URLSearchParams(location.search).get('url') || '';
	const [redirectUrl, setRedirectUrl] = useState(getInitialUrl);

	const [vcard, setVcard] = useState({
		phone: '', mail: '', company: '', firstName: '', lastName: '',
		phoneWork: '', phoneMobile: '', website: '', jobTitle: '',
		street: '', city: '', state: '', zipcode: '', country: '',
		personalNote: '', birthday: '', uuid: null,
	});
	const [lastGeneratedVCard, setLastGeneratedVCard] = useState(null);
	const [lastVCardUuid, setLastVCardUuid] = useState(null);

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [imageBase64, setImageBase64] = useState('');
	const [showDecisionButtons, setShowDecisionButtons] = useState(false);
	const [isDiscarding, setIsDiscarding] = useState(false);
	const [currentUuid, setCurrentUuid] = useState(null);
	const [classicQRTargetUrl, setClassicQRTargetUrl] = useState('');
	const [livePreviewActive, setLivePreviewActive] = useState(false);

	const step1Valid = codeType === 'quikr' ? positivePrompt.trim().length > 0 : true;
	const step3Valid = dataContentType === 'url'
		? redirectUrl.trim().length > 0
		: (vcard.firstName.trim().length > 0 || vcard.lastName.trim().length > 0);

	const ensureHttps = (url) => {
		const tr = url.trim();
		if (!tr) return '';
		if (tr.startsWith('https://')) return tr;
		if (tr.startsWith('http://')) return 'https://' + tr.slice(7);
		return 'https://' + tr;
	};

	const resolveVCardUrl = useCallback(async () => {
		const camelToSnake = (s) => s.replace(/[A-Z]/g, (l) => `_${l.toLowerCase()}`);
		const toSnake = (obj) => {
			if (!obj || typeof obj !== 'object') return obj;
			if (Array.isArray(obj)) return obj.map(toSnake);
			return Object.fromEntries(Object.entries(obj).map(([k, v]) => [camelToSnake(k), toSnake(v)]));
		};
		const same = (a, b) => {
			const { uuid: _, ...ra } = a, { uuid: __, ...rb } = b;
			return JSON.stringify(ra) === JSON.stringify(rb);
		};
		if (lastGeneratedVCard && same(vcard, lastGeneratedVCard))
			return `${CONFIG.BASE_URL}/vcard/${lastVCardUuid}`;
		const payload = { ...toSnake(vcard), name: buildFullName(vcard.firstName, vcard.lastName) };
		const created = await VCardService.create(payload);
		setLastVCardUuid(created.uuid);
		setLastGeneratedVCard(vcard);
		return `${CONFIG.BASE_URL}/vcard/${created.uuid}`;
	}, [vcard, lastGeneratedVCard, lastVCardUuid]);

	const fetchUsageData = async () => {
		try {
			const res = await authFetch(`${CONFIG.BASE_URL}/account`, { method: 'GET', headers: authHeaders() });
			if (res.ok) {
				const data = await res.json();
				if (data.subscription?.qr_code_usage_and_limits)
					setUsageData(data.subscription.qr_code_usage_and_limits);
			}
		} catch {

		} finally { setUsageLoading(false); }
	};

	useEffect(() => { fetchUsageData(); }, []);

	useEffect(() => {
		setNetStrength(quikrMode === 'hd' ? STRENGTH_BOUNDS.hd.default : STRENGTH_BOUNDS.classique.default);
	}, [quikrMode]);

	const generateClassicPreview = useCallback(async (targetUrl, style) => {
		const formData = new FormData();
		formData.append('data', JSON.stringify({ redirect_url: targetUrl, content: targetUrl, 'retrieve-mode': 'base64', ...style }));
		const res = await authFetch(`${CONFIG.BASE_URL}/preview-classic-qr-code`, { method: 'POST', headers: authHeaders(false), body: formData });
		if (!res.ok) throw new Error(`Erreur ${res.status}`);
		const data = await res.json();
		if (!data['qr-code-data']) throw new Error('Aperçu QR indisponible.');
		return data['qr-code-data'];
	}, []);

	useEffect(() => {
		if (!livePreviewActive || codeType !== 'qr' || !classicQRTargetUrl) return;
		let cancelled = false;
		(async () => {
			try {
				const b64 = await generateClassicPreview(classicQRTargetUrl, qrStyle);
				if (!cancelled) setImageBase64(b64);
			} catch {  }
		})();
		return () => { cancelled = true; };
	}, [qrStyle['dots-color'], qrStyle['background-color'], qrStyle['dots-shape'], qrStyle['dot-radius'], qrStyle['dot-spacing'], qrStyle['border-size'], qrStyle['box-size']]);

	const generate = async (e) => {
		e.preventDefault();
		setIsLoading(true);
		setError(''); setSuccess('');
		setCurrentUuid(null); setImageBase64('');
		setShowDecisionButtons(false); setLivePreviewActive(false);

		try {
			const targetUrl = dataContentType === 'url' ? redirectUrl : await resolveVCardUrl();

			if (codeType === 'qr') {
				const b64 = await generateClassicPreview(targetUrl, qrStyle);
				setClassicQRTargetUrl(targetUrl);
				setImageBase64(b64);
				setLivePreviewActive(true);
			} else {
				const payload = {
					generation_prompt: positivePrompt.trim(),
					ai_prompt_augmentation: aiPromptAugmentation,
					item_type: 'ai',
					redirect_url: targetUrl,
					net_strength: parseFloat(netStrength),
				};
				if (negativePrompt.trim()) payload.negative_prompt = negativePrompt.trim();
				if (maxRedirect.trim()) payload.max_redirect = Number(maxRedirect);
				if (validUntil?.trim()) {
					payload.valid_until = new Date(validUntil).toISOString().replace('.000Z', '+00:00');
				}
				if (quikrMode === 'hd') {
					const preset = HD_PRESETS[hdPreset];
					payload.output_size = preset.output_size;
					payload.dpi = preset.dpi;
				}
				const workflow = quikrMode === 'hd' ? 'high_dpi' : 'simple';

				let submission;
				try {
					submission = await QRCodeService.submitQRCodeStylizedJob(payload, workflow);
				} catch (submitErr) {
					if (submitErr.status === 503) throw new Error(content.AiServerError);
					throw new Error(submitErr.message || `Erreur (${submitErr.status})`);
				}

				const uuid = submission?.qr_code_id;
				const jobId = submission?.job_id;
				if (!uuid || !jobId) throw new Error('Identifiant du QR non trouvé.');

				try {
					await QRCodeService.pollQRCodeStylizedJob(jobId);
				} catch (pollErr) {
					if (pollErr.status === 'timeout') throw new Error(content.generationTimeout);
					if (pollErr.status === 503) throw new Error(content.generationFailed);
					throw new Error(pollErr.message || `Erreur (${pollErr.status})`);
				}

				const qrData = await QRCodeService.getQRCodeById(uuid, QRCodeService.MINIATURE_CONFIGS.HIGH_QUALITY);
				const thumb = QRCodeService.getQRThumbnailUrl(qrData);
				if (!thumb) throw new Error('Miniature QR non disponible.');
				setCurrentUuid(uuid);
				setImageBase64(thumb.replace('data:image/webp;base64,', ''));
				fetchUsageData();
			}

			setShowDecisionButtons(true);
			setSuccess(content.successMsg);
		} catch (err) {
			setError(err.message || content.unknownError);
		} finally {
			setIsLoading(false);
		}
	};

	const handleKeepQR = async () => {
		if (codeType === 'qr') {
			try {
				const formData = new FormData();
				const d = { redirect_url: classicQRTargetUrl, content: classicQRTargetUrl, item_type: 'classic', ...qrStyle };
				if (!d.max_redirect) delete d.max_redirect;
				if (d.valid_until?.trim()) d.valid_until = new Date(d.valid_until).toISOString().replace('.000Z', '+00:00');
				else delete d.valid_until;
				formData.append('data', JSON.stringify(d));
				const res = await authFetch(`${CONFIG.BASE_URL}/qr-code`, { method: 'POST', headers: authHeaders(false), body: formData });
				if (!res.ok) throw new Error(`Erreur ${res.status}`);
			} catch (err) {
				setError(err.message || content.saveError || 'Erreur lors de la sauvegarde.');
				return;
			}
		}
		setShowDecisionButtons(false); setLivePreviewActive(false);
		setSuccess(content.keptMsg);
		onUserAcceptsQuikRCode();
	};

	const handleDiscardQR = async () => {
		if (codeType === 'qr') {
			setShowDecisionButtons(false); setLivePreviewActive(false);
			setImageBase64(''); setClassicQRTargetUrl('');
			setSuccess(content.deleteMsg || 'Annulé.');
			return;
		}
		if (!currentUuid) return;
		setIsDiscarding(true);
		try {
			const res = await authFetch(`${CONFIG.BASE_URL}/discard-qr-code/${currentUuid}`, { method: 'PATCH', headers: authHeaders() });
			if (!res.ok) {
				let msg = '';
				try { const j = await res.json(); msg = j.msg || j.error; } catch { msg = await res.text(); }
				throw new Error(msg || `Erreur (${res.status})`);
			}
			setShowDecisionButtons(false); setCurrentUuid(null); setImageBase64('');
			setSuccess(content.deleteMsg);
		} catch (err) {
			setError(err.message || content.saveError);
		} finally { setIsDiscarding(false); }
	};

	const downloadPng = async () => {
		if (codeType === 'qr' && imageBase64) {
			const a = document.createElement('a');
			a.href = `data:image/png;base64,${imageBase64}`;
			a.download = 'QR-CODE.png';
			document.body.appendChild(a); a.click(); a.remove();
			return;
		}
		if (!currentUuid) return;
		try {
			const res = await authFetch(`${CONFIG.BASE_URL}/qr-code/${currentUuid}/download`, { headers: authHeaders() });
			if (!res.ok) throw new Error(`Téléchargement impossible (${res.status}).`);
			const blob = await res.blob();
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			const cd = res.headers.get('Content-Disposition');
			let fn = `QR-CODE-${currentUuid}.png`;
			if (cd?.includes('filename=')) fn = cd.split('filename=')[1].split(';')[0].replace(/"/g, '');
			a.href = url; a.download = fn;
			document.body.appendChild(a); a.click(); a.remove();
			URL.revokeObjectURL(url);
		} catch (err) { setError(err.message || content.downloadError); }
	};

	const promptHint = useTypist(
		useMemo(() => content?.typistPrompts || [], [content]),
		useMemo(() => ({ typeSpeed: 10, deleteSpeed: 30, holdDelay: 8000 }), []),
		'prompt'
	);
	const linkHint = useTypist(
		useMemo(() => content?.typistLinks || [], [content]),
		useMemo(() => ({ typeSpeed: 20, deleteSpeed: 50, holdDelay: 8000 }), []),
		'link'
	);

	if (!content) {
		return (
			<div className={`border rounded-xl overflow-hidden w-full ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-gray-200 shadow-sm'} p-4 md:p-6`}>
				<div className="animate-pulse space-y-4">
					{[...Array(3)].map((_, i) => <div key={i} className={`h-10 rounded ${isDark ? 'bg-dark-700' : 'bg-gray-300'}`} />)}
				</div>
			</div>
		);
	}

	const httpsLinkHint = linkHint ? `https://${linkHint}` : 'https://';

	const step1Summary = codeType === 'quikr'
		? `${content.codeTypeQuikr ?? 'QuikR Code'} (${quikrMode === 'hd' ? `HD — ${HD_PRESETS[hdPreset].label}` : 'Classique'})${positivePrompt.trim() ? ` — "${positivePrompt.trim().slice(0, 30)}${positivePrompt.length > 30 ? '…' : ''}"` : ''}`
		: (content.codeTypeQr ?? 'QR Code');

	const qrTabs = [
		{ id: 'config', label: t.tabs.configuration },
		{ id: 'colors', label: t.tabs.colors },
		{ id: 'shapes', label: t.tabs.shapes },
	];

	const QrCustomizationPanel = () => (
		<div className="space-y-3 mt-3">
			<div className={`flex gap-1 p-1 rounded-lg ${isDark ? 'bg-dark-700' : 'bg-gray-200'}`}>
				{qrTabs.map((tab) => (
					<button key={tab.id} type="button" onClick={() => setActiveQrTab(tab.id)}
						className={`flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-colors ${
							activeQrTab === tab.id ? 'bg-[rgb(13,200,207)] text-white'
							: isDark ? 'text-gray-400 hover:text-white hover:bg-dark-600'
							: 'text-gray-600 hover:text-gray-900 hover:bg-gray-300'
						}`}>
						{tab.label}
					</button>
				))}
			</div>

			{activeQrTab === 'config' && (
				<div className="space-y-4">
					<div>
						<label className={`form-label text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>
							{t.fields.boxSizeLabel} <span className="text-[rgb(13,200,207)] font-semibold">{qrStyle['box-size']}</span>
						</label>
						<input type="range" min="5" max="25" value={qrStyle['box-size']}
							onChange={(e) => setQrStyle((p) => ({ ...p, 'box-size': parseInt(e.target.value) }))}
							className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${isDark ? 'bg-dark-600' : 'bg-gray-300'}`} />
					</div>
					<div>
						<label className={`form-label text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>
							{t.fields.borderSizeLabel} <span className="text-[rgb(13,200,207)] font-semibold">{qrStyle['border-size']}</span>
						</label>
						<input type="range" min="1" max="10" value={qrStyle['border-size']}
							onChange={(e) => setQrStyle((p) => ({ ...p, 'border-size': parseInt(e.target.value) }))}
							className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${isDark ? 'bg-dark-600' : 'bg-gray-300'}`} />
					</div>
					<div className={isDark ? 'text-white' : 'text-gray-900'}>
						<label className="form-label text-sm">{t.fields.maxRedirectLabel}</label>
						<input type="number" className="form-input" placeholder={t.fields.maxRedirectPlaceholder} min="1"
							value={qrStyle['max_redirect']}
							onChange={(e) => setQrStyle((p) => ({ ...p, max_redirect: e.target.value }))} />
					</div>
					<div className={isDark ? 'text-white' : 'text-gray-900'}>
						<label className="form-label text-sm">{t.fields.validUntilLabel}</label>
						<input type="datetime-local" className="form-input" value={qrStyle['valid_until']}
							onChange={(e) => setQrStyle((p) => ({ ...p, valid_until: e.target.value }))} />
						<p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t.fields.validUntilHint}</p>
					</div>
				</div>
			)}

			{activeQrTab === 'colors' && (
				<div className="space-y-4">
					<div className={isDark ? 'text-white' : 'text-gray-900'}>
						<label className="form-label text-sm">{t.fields.dotsColorLabel}</label>
						<input type="color" value={qrStyle['dots-color']}
							onChange={(e) => setQrStyle((p) => ({ ...p, 'dots-color': e.target.value }))}
							className="w-full h-12 rounded-lg cursor-pointer border-2 border-dark-600" />
					</div>
					<div className={isDark ? 'text-white' : 'text-gray-900'}>
						<label className="form-label text-sm">{t.fields.bgColorLabel}</label>
						<input type="color" value={qrStyle['background-color']}
							onChange={(e) => setQrStyle((p) => ({ ...p, 'background-color': e.target.value }))}
							className="w-full h-12 rounded-lg cursor-pointer border-2 border-dark-600" />
					</div>
				</div>
			)}

			{activeQrTab === 'shapes' && (
				<div className="space-y-4">
					<div className={isDark ? 'text-white' : 'text-gray-900'}>
						<label className="form-label text-sm">{t.fields.dotsShapeLabel}</label>
						<select className="form-input" value={qrStyle['dots-shape']}
							onChange={(e) => setQrStyle((p) => ({ ...p, 'dots-shape': e.target.value }))}>
							<option value="square">{t.shapes.square}</option>
							<option value="diamond">{t.shapes.diamond}</option>
							<option value="triangle">{t.shapes.triangle}</option>
							<option value="pentagon">{t.shapes.pentagon}</option>
							<option value="hexagon">{t.shapes.hexagon}</option>
							<option value="octagon">{t.shapes.octagon}</option>
							<option value="star">{t.shapes.star}</option>
							<option value="heart">{t.shapes.heart}</option>
						</select>
					</div>
					<div>
						<label className={`form-label text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>
							{t.fields.cornerRadiusLabel} <span className="text-[rgb(13,200,207)] font-semibold">{qrStyle['dot-radius']}</span>
						</label>
						<input type="range" min="0" max="10" value={qrStyle['dot-radius']}
							onChange={(e) => setQrStyle((p) => ({ ...p, 'dot-radius': parseInt(e.target.value) }))}
							className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${isDark ? 'bg-dark-600' : 'bg-gray-300'}`} />
					</div>
					<div>
						<label className={`form-label text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>
							{t.fields.spacingLabel} <span className="text-[rgb(13,200,207)] font-semibold">{qrStyle['dot-spacing']}</span>
						</label>
						<input type="range" min="0" max="5" value={qrStyle['dot-spacing']}
							onChange={(e) => setQrStyle((p) => ({ ...p, 'dot-spacing': parseInt(e.target.value) }))}
							className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${isDark ? 'bg-dark-600' : 'bg-gray-300'}`} />
					</div>
				</div>
			)}
		</div>
	);


	return (
		<>
		<style>{`
			@keyframes stepReveal {
				from { opacity: 0; transform: translateY(10px); }
				to   { opacity: 1; transform: translateY(0); }
			}
			.step-enter { animation: stepReveal 0.3s ease forwards; }

			input[type="range"].strength-slider {
				-webkit-appearance: none;
				appearance: none;
				height: 8px;
				border-radius: 9999px;
				background: var(--slider-track-bg, #d1d5db);
				outline: none;
			}
			input[type="range"].strength-slider::-webkit-slider-runnable-track {
				height: 8px;
				border-radius: 9999px;
				background: transparent;
			}
			input[type="range"].strength-slider::-webkit-slider-thumb {
				-webkit-appearance: none;
				appearance: none;
				width: 18px;
				height: 18px;
				margin-top: -5px;
				border-radius: 9999px;
				background: #ffffff;
				border: 3px solid rgb(13,200,207);
				box-shadow: 0 1px 3px rgba(0,0,0,0.35);
				cursor: pointer;
			}
			input[type="range"].strength-slider::-moz-range-track {
				height: 8px;
				border-radius: 9999px;
				background: var(--slider-track-bg, #d1d5db);
			}
			input[type="range"].strength-slider::-moz-range-thumb {
				width: 18px;
				height: 18px;
				border: 3px solid rgb(13,200,207);
				border-radius: 9999px;
				background: #ffffff;
				box-shadow: 0 1px 3px rgba(0,0,0,0.35);
				cursor: pointer;
			}
			input[type="range"].strength-slider::-moz-range-progress {
				background: transparent;
			}
		`}</style>

		<div className={`border rounded-xl overflow-hidden w-full ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-gray-200 shadow-sm'}`}>

			<div className={`p-4 md:p-6 border-b ${isDark ? 'border-dark-700' : 'border-gray-200'}`}>
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
					<div>
						<h2 className={`text-lg md:text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{content.title}</h2>
						<p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{content.subtitle}</p>
					</div>
					{!usageLoading && usageData && codeType === 'quikr' && completedUpTo >= 1 && (
						<div className={`flex items-center gap-2 px-3 py-2 rounded-lg shrink-0 ${isDark ? 'bg-dark-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
							<span className="text-sm font-medium">Essais restants :</span>
							<span className={`text-sm font-bold ${
								usageData.max_qr_code_ai_generation_try_monthly_limit === -1 ? 'text-green-500'
								: (usageData.max_qr_code_ai_generation_try_monthly_limit - usageData.qr_code_ai_generation_count) <= 5 ? 'text-orange-500'
								: 'text-primary-500'
							}`}>
								{usageData.max_qr_code_ai_generation_try_monthly_limit === -1 ? 'Illimité'
									: `${Math.max(0, usageData.max_qr_code_ai_generation_try_monthly_limit - usageData.qr_code_ai_generation_count)} / ${usageData.max_qr_code_ai_generation_try_monthly_limit}`}
							</span>
						</div>
					)}
				</div>
			</div>

			<form onSubmit={generate} className="p-4 md:p-6 flex flex-col lg:grid lg:grid-cols-[1fr_auto] gap-6 md:gap-8">

				<div className="flex flex-col gap-3">

					<StepCard isDark={isDark}>
						{stepStatus(1) === 'completed' ? (
							<StepSummary n={1} title={content.codeTypeLabel ?? 'Type de code'}
								summary={step1Summary} isDark={isDark} onEdit={() => editStep(1)} />
						) : (
							<>
								<StepTitle n={1} title={content.codeTypeLabel ?? 'Type de code'} isDark={isDark} />

								<TileSelector
									name="codeType"
									options={[
										{ id: 'quikr', label: content.codeTypeQuikr ?? 'QuikR Code' },
										{ id: 'qr',    label: content.codeTypeQr    ?? 'QR Code' },
									]}
									value={codeType}
									onChange={setCodeType}
								/>

								{codeType === 'quikr' && (
									<div className="mt-3 space-y-3">
										<p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
											Mode de génération
										</p>
										<div className="flex gap-3">
											{[
												{ id: 'classique', label: 'Classique' },
												{ id: 'hd',        label: 'HD', locked: !canUseHD },
											].map(({ id, label, locked }) => (
												<button
													key={id}
													type="button"
													disabled={locked}
													onClick={() => !locked && setQuikrMode(id)}
													className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
														locked
															? `opacity-40 cursor-not-allowed ${isDark ? 'border-dark-600 text-gray-500' : 'border-gray-200 text-gray-400'}`
															: quikrMode === id
																? 'bg-[rgb(13,200,207)] text-white border-transparent'
																: isDark
																	? 'border-dark-600 text-gray-300 hover:border-gray-400 bg-transparent'
																	: 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
													}`}
												>
													{label}{locked ? ' 🔒' : id === 'hd' ? ' ⏱️' : ''}
												</button>
											))}
										</div>

										{quikrMode === 'hd' && (
											<div className="step-enter space-y-3">
												<p className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
													Résolution d'impression
												</p>
												<div className="flex flex-wrap gap-2">
													{Object.entries(HD_PRESETS).map(([key, preset]) => (
														<button
															key={key}
															type="button"
															onClick={() => setHdPreset(key)}
															className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition ${
																hdPreset === key
																	? 'bg-[rgb(13,200,207)] text-white border-transparent'
																	: isDark
																		? 'border-dark-600 text-gray-300 hover:border-gray-400 bg-transparent'
																		: 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
															}`}
														>
															{preset.label}
														</button>
													))}
												</div>
												<div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${
													isDark ? 'bg-amber-900/20 text-amber-300 border border-amber-800/40' : 'bg-amber-50 text-amber-700 border border-amber-200'
												}`}>
													<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
														<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
													</svg>
													<span>{ai.fields.hdSlowHint ?? 'La génération HD est plus détaillée et peut prendre plusieurs minutes.'}</span>
												</div>
											</div>
										)}
									</div>
								)}

								{codeType === 'quikr' && (
									<div className="mt-4 space-y-4 step-enter">

										<div className={`flex items-center justify-between p-3 rounded-lg border ${
											isDark ? 'bg-dark-900 border-dark-700' : 'bg-white border-gray-200'
										}`}>
											<div className="flex-1">
												<p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
													{ai.fields.promptAugLabel}
												</p>
												<p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
													{ai.fields.promptAugHint}
												</p>
											</div>
											<button
												type="button"
												role="switch"
												aria-checked={aiPromptAugmentation}
												onClick={() => setAiPromptAugmentation((p) => !p)}
												className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ml-4 shrink-0 ${
													aiPromptAugmentation
														? 'bg-gradient-to-r from-primary-500 to-secondary-500'
														: isDark ? 'bg-dark-600' : 'bg-gray-300'
												}`}
											>
												<span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
													aiPromptAugmentation ? 'translate-x-6' : 'translate-x-1'
												}`} />
											</button>
										</div>

										<div className={isDark ? 'text-white' : 'text-gray-900'}>
											<label className="form-label text-sm" htmlFor="promptPos">
												{ai.fields.positivePromptLabel}
											</label>
											<textarea
												id="promptPos"
												className="form-input min-h-[90px]"
												placeholder={ai.fields.positivePromptPlaceholder}
												value={positivePrompt}
												onChange={(e) => setPositivePrompt(e.target.value)}
											/>
										</div>

										<div className={isDark ? 'text-white' : 'text-gray-900'}>
											<label className="form-label text-sm" htmlFor="promptNeg">
												{ai.fields.negativePromptLabel}
											</label>
											<textarea
												id="promptNeg"
												className="form-input min-h-[70px]"
												placeholder={ai.fields.negativePromptPlaceholder}
												value={negativePrompt}
												onChange={(e) => setNegativePrompt(e.target.value)}
											/>
										</div>

										<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
											<div className={isDark ? 'text-white' : 'text-gray-900'}>
												<label className="form-label text-sm">{ai.fields.maxUsageLabel}</label>
												<input type="number" className="form-input" min="1" placeholder="Ex: 100"
													value={maxRedirect}
													onChange={(e) => setMaxRedirect(e.target.value)} />
											</div>
											<div className={isDark ? 'text-white' : 'text-gray-900'}>
												<label className="form-label text-sm">{ai.fields.validUntilLabel}</label>
												<input type="datetime-local" className="form-input"
													value={validUntil}
													onChange={(e) => setValidUntil(e.target.value)} />
											</div>
										</div>

										{(() => {
											const bounds = quikrMode === 'hd' ? STRENGTH_BOUNDS.hd : STRENGTH_BOUNDS.classique;
											const strengthValue = parseFloat(netStrength);
											const zone = getStrengthZone(strengthValue, bounds);
											const rawPct = ((strengthValue - bounds.min) / (bounds.max - bounds.min)) * 100;
											const thumbPct = Math.min(96, Math.max(4, rawPct));
											return (
												<div className={isDark ? 'text-white' : 'text-gray-900'}>
													<label className="form-label text-sm">
														{quikrMode === 'hd' ? (ai.fields.strengthLabelHd ?? ai.fields.strengthLabel) : ai.fields.strengthLabel}
													</label>
													<div className="relative pt-7">
														<div
															className="absolute top-0 flex flex-col items-center pointer-events-none"
															style={{ left: `${thumbPct}%`, transform: 'translateX(-50%)' }}
														>
															<span className="px-2 py-0.5 rounded-md text-xs font-semibold text-white bg-[rgb(13,200,207)] shadow whitespace-nowrap">
																{netStrength}
															</span>
															<span className="w-2 h-2 -mt-[3px] rotate-45 bg-[rgb(13,200,207)]" />
														</div>
														<input
															type="range"
															className="w-full strength-slider"
															min={bounds.min}
															max={bounds.max}
															step="0.01"
															value={netStrength}
															onChange={(e) => setNetStrength(e.target.value)}
															style={{ '--slider-track-bg': buildStrengthGradient(bounds) }}
														/>
													</div>
													{zone === 'low' && (
														<p className={`text-xs mt-1.5 ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>
															⚠️ {ai.fields.strengthLowWarning ?? "À ce niveau, le QuikR Code risque de manquer de lisibilité et d'être plus difficile à scanner."}
														</p>
													)}
													{zone === 'high' && (
														<p className={`text-xs mt-1.5 ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>
															⚠️ {ai.fields.strengthHighWarning ?? "À ce niveau, l'image générée risque d'être plus discrète, dominée par le motif du QR Code."}
														</p>
													)}
												</div>
											);
										})()}

									</div>
								)}

								{codeType === 'qr' && (
									<div className="step-enter">
										<QrCustomizationPanel />
									</div>
								)}

								<ContinueBtn onClick={() => { advanceTo(1); if (dataContentType) advanceTo(2); }} disabled={!step1Valid} />
							</>
						)}
					</StepCard>

					{stepStatus(2) !== 'hidden' && (
						<StepCard isDark={isDark}>
							{stepStatus(2) === 'completed' ? (
								<StepSummary n={2} title={content.typeLabel}
									summary={dataContentType === 'url' ? 'URL' : 'VCard'}
									isDark={isDark} onEdit={() => editStep(2)} />
							) : (
								<>
									<StepTitle n={2} title={content.typeLabel} isDark={isDark} />
									<TileSelector
										name="dataType"
										options={[
											{ id: 'url',   label: 'URL' },
											{ id: 'vcard', label: 'VCard' },
										]}
										value={dataContentType}
										onChange={(val) => { setDataContentType(val); advanceTo(2); }}
									/>
								</>
							)}
						</StepCard>
					)}

					{stepStatus(3) !== 'hidden' && (
						<StepCard isDark={isDark}>
							{stepStatus(3) === 'completed' ? (
								<StepSummary n={3}
									title={dataContentType === 'url' ? content.urlLabel : content.vcardLabel}
									summary={
										dataContentType === 'url'
											? redirectUrl.trim()
											: (buildFullName(vcard.firstName, vcard.lastName) || vcard.mail || '—')
									}
									isDark={isDark} onEdit={() => editStep(3)} />
							) : (
								<>
									<StepTitle n={3}
										title={dataContentType === 'url' ? content.urlLabel : content.vcardLabel}
										isDark={isDark} />
									{dataContentType === 'url' ? (
										<input className="form-input" type="url"
											placeholder={httpsLinkHint}
											value={redirectUrl}
											onChange={(e) => setRedirectUrl(e.target.value)}
											onBlur={() => { if (redirectUrl.trim()) setRedirectUrl(ensureHttps(redirectUrl)); }}
										/>
									) : (
										<VCardForm vcard={vcard} setVcard={setVcard} content={content} isDark={isDark} />
									)}
									<ContinueBtn onClick={() => advanceTo(3)} disabled={!step3Valid} />
								</>
							)}
						</StepCard>
					)}

					{stepStatus(4) !== 'hidden' && (
						<StepCard isDark={isDark}>
							<StepTitle n={4} title={content.generateBtn ?? 'Générer'} isDark={isDark} />

							{!showDecisionButtons ? (
								<button type="submit" disabled={isLoading}
									className="w-full btn-primary bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-black disabled:opacity-50 disabled:cursor-not-allowed">
									{isLoading ? content.generating : content.generateBtn}
								</button>
							) : (
								<button type="submit" disabled={isLoading}
									className="w-full py-2 rounded-lg text-sm font-medium border border-[rgb(13,200,207)] text-[rgb(13,200,207)] hover:bg-[rgb(13,200,207)] hover:text-white disabled:opacity-50 transition-colors">
									↺ {content.generateBtn}
								</button>
							)}
						</StepCard>
					)}

					{error && (
						<div className={`text-sm border rounded-lg p-3 step-enter ${isDark ? 'text-red-300 bg-red-900/40 border-red-700' : 'text-red-600 bg-red-50 border-red-200'}`}>{error}</div>
					)}
					{success && (
						<div className={`text-sm border rounded-lg p-3 step-enter ${isDark ? 'text-green-300 bg-green-900/30 border-green-700' : 'text-green-600 bg-green-50 border-green-200'}`}>{success}</div>
					)}

					{showDecisionButtons && imageBase64 && !isLoading && !isMobile && (
						<DecisionButtons
							onKeep={handleKeepQR} onDiscard={handleDiscardQR}
							isDiscarding={isDiscarding} isDark={isDark}
							keepQuestion={content.keepQuestion}
						/>
					)}
				</div>

				<div className="lg:order-last flex flex-col">
					<div className={`border rounded-xl aspect-square p-4 flex items-center justify-center min-h-[250px] lg:min-h-[440px] max-w-[440px] max-h-[440px] ${isDark ? 'bg-dark-900 border-dark-700' : 'bg-gray-50 border-gray-200'}`}>
						{isLoading ? (
							<div className="text-center">
								<div className="relative w-24 h-24 mx-auto mb-6">
									<div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary-500 border-r-secondary-500 animate-spin" />
									<div className="absolute inset-2 rounded-full border-4 border-transparent border-b-primary-400 border-l-secondary-400 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
									<div className="absolute inset-0 flex items-center justify-center">
										<div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 animate-pulse" />
									</div>
								</div>
								<p className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Génération en cours...</p>
								{codeType === 'quikr' && quikrMode === 'hd' && (
									<p className={`text-xs max-w-[240px] mx-auto mb-2 ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>
										⏱️ {ai.fields.hdGeneratingHint ?? 'La génération HD peut prendre plusieurs minutes, merci de patienter.'}
									</p>
								)}
								<div className="flex justify-center space-x-1 mt-4">
									{[0, 150, 300].map((d) => (
										<div key={d} className="w-2 h-2 rounded-full bg-primary-500 animate-bounce" style={{ animationDelay: `${d}ms` }} />
									))}
								</div>
							</div>
						) : imageBase64 ? (
							<img src={`data:image/png;base64,${imageBase64}`} alt="Code généré"
								className="w-full max-w-[512px] h-auto rounded-lg shadow-lg" />
						) : (
							<div className={`w-full max-w-[512px] text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
								<img
								src={codeType === 'qr' ? '/images/qrcode.png' : '/images/qr-ai-example-3.png'}
								alt="Exemple"
								className="p-4 h-auto rounded-lg shadow-lg"
							/>
								<p>{codeType === 'qr' ? content.qrCodeExempleLabel : content.quikrCodeExempleLabel}</p>
							</div>
						)}
					</div>

					{imageBase64 && !showDecisionButtons && (
						<button type="button" onClick={downloadPng}
							className="mt-3 btn-secondary border-primary-500 text-primary-400 hover:bg-primary-500 hover:text-black">
							{content.downloadBtn}
						</button>
					)}

					{showDecisionButtons && imageBase64 && !isLoading && isMobile && (
						<div className="mt-3">
							<DecisionButtons
								onKeep={handleKeepQR} onDiscard={handleDiscardQR}
								isDiscarding={isDiscarding} isDark={isDark}
								keepQuestion={content.keepQuestion}
							/>
						</div>
					)}
				</div>
			</form>
		</div>
		</>
	);
};

export default SimplifiedAIQREditor;
